import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { OperatorsService } from '../../operators/operators.service';

@Component({
    selector: 'app-view-project',
    templateUrl: './view-project.modal.component.html',
    styleUrls: ['./view-project.modal.component.scss'],
})
export class ViewProjectModalComponent implements OnInit {
    private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
    private _project: any;

    @Input() set project(value: any) {
        this._project = value;
        if (value) {
            this.patchInitialValues();
        }
    }

    get project(): any {
        return this._project;
    }

    public isProjectExpanded = true;
    public isCompanyExpanded = false;
    public isAddressExpanded = false;
    public isSponsorExpanded = false;
    public isFinancialExpanded = false;
    public isPermissionsExpanded = false;
    public isListCnpjExpanded = false;
    public isIpExpanded = false;
    public isCertificateExpanded = false;

    public accountGuid = '';
    public insertDate: any = null;
    public updateDate: any = null;

    public licensingsLoading = false;
    public licensingMap: { [id: string]: string } = {};
    public licensingId = '';
    public licensingFallbackLabel = '-';

    viewForm = new FormGroup({
        id: new FormControl<string>('', { nonNullable: true }),

        operatorRegistration: new FormGroup({
            id: new FormControl<string>('', { nonNullable: true }),
            nameProject: new FormControl<string>('', { nonNullable: true }),
            site: new FormControl<string>('', { nonNullable: true }),
            productId: new FormControl<string>('', { nonNullable: true }),
            bankProvider: new FormControl<string>('', { nonNullable: true }),
            currentPlatform: new FormControl<string>('', { nonNullable: true }),
            percentGGR: new FormControl<number>(0, { nonNullable: true }),
        }),

        company: new FormGroup({
            cnpj: new FormControl<string>('', { nonNullable: true }),
            razaoSocial: new FormControl<string>('', { nonNullable: true }),
            email: new FormControl<string>('', { nonNullable: true }),
            phone: new FormControl<string>('', { nonNullable: true }),
        }),

        adress: new FormGroup({
            logradouro: new FormControl<string>('', { nonNullable: true }),
            numero: new FormControl<string>('', { nonNullable: true }),
            complemento: new FormControl<string>('', { nonNullable: true }),
            cep: new FormControl<string>('', { nonNullable: true }),
            bairro: new FormControl<string>('', { nonNullable: true }),
            cidade: new FormControl<string>('', { nonNullable: true }),
            estado: new FormControl<string>('', { nonNullable: true }),
        }),

        sponsor: new FormGroup({
            cpf: new FormControl<string>('', { nonNullable: true }),
            name: new FormControl<string>('', { nonNullable: true }),
            email: new FormControl<string>('', { nonNullable: true }),
            phone: new FormControl<string>('', { nonNullable: true }),
        }),

        depositValueMinRequest: new FormControl<number>(0, { nonNullable: true }),
        depositValueMaxRequest: new FormControl<number>(0, { nonNullable: true }),
        valorMinimoSaqueAutomatico: new FormControl<number>(0, { nonNullable: true }),
        amountExpired: new FormControl<number>(0, { nonNullable: true }),
        valueExpired: new FormControl<number>(0, { nonNullable: true }),
        analyzeWithdrawal: new FormControl<boolean>(false, { nonNullable: true }),
        valueMinRequest: new FormControl<number>(0, { nonNullable: true }),
        valueMaxRequest: new FormControl<number>(0, { nonNullable: true }),

        permissonWithdraw: new FormControl<boolean>(false, { nonNullable: true }),
        permissonDeposit: new FormControl<boolean>(false, { nonNullable: true }),
        permissonTef: new FormControl<boolean>(false, { nonNullable: true }),
        permissonCrypto: new FormControl<boolean>(false, { nonNullable: true }),
        permissionGameApi: new FormControl<boolean>(false, { nonNullable: true }),
        validateIp: new FormControl<boolean>(false, { nonNullable: true }),
        blackList: new FormControl<boolean>(false, { nonNullable: true }),

        featureFlagRefundPix: new FormControl<boolean>(false, { nonNullable: true }),
        featureFlagCpfWithdraw: new FormControl<boolean>(false, { nonNullable: true }),
        featureFlagCustomerBalance: new FormControl<boolean>(false, { nonNullable: true }),
        featureFlagNewParam: new FormControl<boolean>(false, { nonNullable: true }),

        userBlackList: new FormControl<boolean>(false, { nonNullable: true }),

        listsIp: new FormControl<string[]>([]),

        mtls: new FormGroup({
            deposit: new FormControl<boolean>(false, { nonNullable: true }),
            withdraw: new FormControl<boolean>(false, { nonNullable: true }),
            tef: new FormControl<boolean>(false, { nonNullable: true }),
        }),

        listCnpj: new FormGroup({
            flagCnpjCashIn: new FormControl<boolean>(false, { nonNullable: true }),
            flagCnpjCashOut: new FormControl<boolean>(false, { nonNullable: true }),
            cnpjCashIn: new FormControl<string[]>([]),
            cnpjCashOut: new FormControl<string[]>([]),
        }),
    });

    constructor(
        public activeModal: NgbActiveModal,
        private toastr: ToastrService,
        private operatorsService: OperatorsService
    ) { }

    ngOnInit(): void {
        this.loadLicensings();
        this.patchInitialValues();
    }

    dismiss(): void {
        this.activeModal.dismiss();
    }

    openSection(section: string): void {
        this.isProjectExpanded = section === 'project';
        this.isCompanyExpanded = section === 'company';
        this.isAddressExpanded = section === 'address';
        this.isSponsorExpanded = section === 'sponsor';
        this.isFinancialExpanded = section === 'financial';
        this.isPermissionsExpanded = section === 'permissions';
        this.isListCnpjExpanded = section === 'listCnpj';
        this.isIpExpanded = section === 'ip';
        this.isCertificateExpanded = section === 'certificate';
    }

    private toNumber(v: any): number {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    }

    private patchInitialValues(): void {
        const src = this.project ?? {};
        const op = src?.operatorRegistration ?? {};
        const company = src?.company ?? {};
        const adress = src?.adress ?? {};
        const sponsor = src?.sponsor ?? {};
        const deposit = src?.depositConfiguration ?? {};
        const withdraw = src?.withDrawConfiguration ?? {};
        const permission = src?.permissonConfiguration ?? {};
        const listCnpj = src?.listCnpj ?? {};

        const mtlsList = Array.isArray(src?.mtls)
            ? src.mtls.map((x: any) => (x ?? '').toString().trim().toLowerCase())
            : [];

        this.accountGuid = (src?.id ?? '').toString();
        this.insertDate = src?.insertDate ?? null;
        this.updateDate = src?.updateDate ?? null;
        this.licensingId = this.resolveLicensingId(src, op);
        this.licensingFallbackLabel = this.resolveLicensingLabel(src, op);

        this.viewForm.patchValue({
            id: (src?.id ?? '').toString(),

            operatorRegistration: {
                id: (op?.id ?? '').toString(),
                nameProject: (op?.nameProject ?? '').toString(),
                site: (op?.site ?? '').toString(),
                productId: (op?.productId ?? '').toString(),
                bankProvider: (op?.bankProvider ?? '').toString(),
                currentPlatform: (op?.currentPlatform ?? '').toString(),
                percentGGR: this.toNumber(op?.percentGGR),
            },

            company: {
                cnpj: (company?.cnpj ?? '').toString(),
                razaoSocial: (company?.razaoSocial ?? '').toString(),
                email: (company?.email ?? '').toString(),
                phone: (company?.phone ?? '').toString(),
            },

            adress: {
                logradouro: (adress?.logradouro ?? '').toString(),
                numero: (adress?.numero ?? '').toString(),
                complemento: (adress?.complemento ?? '').toString(),
                cep: (adress?.cep ?? '').toString(),
                bairro: (adress?.bairro ?? '').toString(),
                cidade: (adress?.cidade ?? '').toString(),
                estado: (adress?.estado ?? '').toString(),
            },

            sponsor: {
                cpf: (sponsor?.cpf ?? '').toString(),
                name: (sponsor?.name ?? '').toString(),
                email: (sponsor?.email ?? '').toString(),
                phone: (sponsor?.phone ?? '').toString(),
            },

            depositValueMinRequest: this.toNumber(deposit?.valueMinRequest),
            depositValueMaxRequest: this.toNumber(deposit?.valueMaxRequest),
            valorMinimoSaqueAutomatico: this.toNumber(withdraw?.valorMinimoSaqueAutomatico),
            amountExpired: this.toNumber(withdraw?.amountExpired),
            valueExpired: this.toNumber(withdraw?.valueExpired),
            analyzeWithdrawal: !!withdraw?.analyzeWithdrawal,
            valueMinRequest: this.toNumber(withdraw?.valueMinRequest),
            valueMaxRequest: this.toNumber(withdraw?.valueMaxRequest),

            permissonWithdraw: !!permission?.permissonWithdraw,
            permissonDeposit: !!permission?.permissonDeposit,
            permissonTef: !!permission?.permissonTef,
            permissonCrypto: !!permission?.permissonCrypto,
            permissionGameApi: !!permission?.permissionGameApi,
            validateIp: !!src?.validateIp,
            blackList: !!src?.blackList,

            featureFlagRefundPix: !!src?.featureFlagRefundPix,
            featureFlagCpfWithdraw: !!src?.featureFlagCpfWithdraw,
            featureFlagCustomerBalance: !!src?.featureFlagCustomerBalance,
            featureFlagNewParam: !!src?.featureFlagNewParam,

            userBlackList: !!src?.userBlackList,

            listsIp: Array.isArray(src?.listsIp) ? src.listsIp : [],

            mtls: {
                deposit: mtlsList.includes('deposit'),
                withdraw: mtlsList.includes('withdraw'),
                tef: mtlsList.includes('tef'),
            },

            listCnpj: {
                flagCnpjCashIn: !!listCnpj?.flagCnpjCashIn,
                flagCnpjCashOut: !!listCnpj?.flagCnpjCashOut,
                cnpjCashIn: Array.isArray(listCnpj?.cnpjCashIn) ? listCnpj.cnpjCashIn : [],
                cnpjCashOut: Array.isArray(listCnpj?.cnpjCashOut) ? listCnpj.cnpjCashOut : [],
            },
        });
    }

    copyGuid(): void {
        const value = (this.accountGuid ?? '').trim();
        if (!value) return;

        navigator.clipboard.writeText(value).then(() => {
            this.toastr.success('GUID copiado com sucesso!', 'Sucesso', {
                timeOut: 2000,
                positionClass: 'toast-top-right',
            });
        });
    }

    loadLicensings(): void {
        if (this.licensingsLoading) return;

        this.licensingsLoading = true;

        this.operatorsService
            .getLicensingRegistration(1000, 1)
            .pipe(finalize(() => (this.licensingsLoading = false)))
            .subscribe({
                next: (value: any) => {
                    const list = (value?.items ?? value?.data ?? value?.result ?? value ?? []) as any[];

                    const map: { [id: string]: string } = {};
                    for (const item of list) {
                        const id = this.normalizeLicensingId(item?.id);
                        const name = (item?.description ?? item?.razaoSocial ?? item?.cnpj ?? '').toString().trim();
                        if (id && name) {
                            map[id] = name;
                        }
                    }

                    this.licensingMap = map;
                },
                error: () => {
                    this.licensingMap = {};
                },
            });
    }

    licensingLabel(licensingId: any): string {
        const id = this.normalizeLicensingId(licensingId);
        if (!id) return this.licensingFallbackLabel || '-';
        return this.licensingMap[id] || this.licensingFallbackLabel || '-';
    }

    private resolveLicensingId(src: any, op: any): string {
        return this.normalizeLicensingId(
            op?.licensingRegistrationId ??
            src?.licensingRegistrationId ??
            op?.licensingRegistration?.id ??
            src?.licensingRegistration?.id ??
            ''
        );
    }

    private resolveLicensingLabel(src: any, op: any): string {
        const licensing = op?.licensingRegistration ?? src?.licensingRegistration ?? {};

        return (
            licensing?.description ??
            licensing?.razaoSocial ??
            licensing?.cnpj ??
            '-'
        ).toString();
    }

    private normalizeLicensingId(value: any): string {
        const id = (value ?? '').toString().trim();
        if (!id || id === this.emptyGuid) {
            return '';
        }

        return id;
    }
}
